/**
 * Language and direction for the whole app (plan.md §54, Phase 2).
 *
 * Two things happen here and only here:
 *
 *  1. **`dir` is set on `<html>`**, not on a wrapper. The browser's own
 *     bidirectional algorithm keys off the document direction — set it on a
 *     div and text still reflows, but native scrollbars, form controls, text
 *     selection and the direction of `Home`/`End` do not. Half-RTL is worse
 *     than none, because it looks deliberate.
 *  2. **`lang` is set with it**, which is what makes a screen reader switch
 *     voice and what lets the browser hyphenate. Setting `dir` alone is the
 *     usual half of this that gets forgotten.
 *
 * The choice is remembered per edition, like every other client preference
 * (hard rule 6). Someone who reads Hebrew at OZORA does not necessarily want
 * Hebrew at a festival that has none.
 *
 * ## The interface was English-only until 2026-09-27 — the history below is why
 *
 * `t()` translates **edition content** — act names, blurbs, info pages — from
 * translations a festival supplied. The app's own chrome is a different thing
 * entirely: "Sign in", "Email or username", "Forgot password" are English
 * literals in the components, and there is no translation table for them.
 *
 * So until that table exists, negotiating the document into Hebrew can only
 * produce a right-to-left page of English words — which is what it did until
 * 2026-09-14. An Israeli browser reports `he-IL`, `negotiateLocale` returned
 * `he`, `dir="rtl"` went on `<html>`, and the sign-in screen rendered mirrored
 * with full stops migrating to the starts of sentences. The note above says
 * half-RTL is worse than none because it looks deliberate; this was that, with
 * the halves swapped.
 *
 * ## Since 2026-10-22 (step 73): a language AND a region, from where you are
 *
 * The user: "en-us / he-us / en-il … all languages and all regions. Default
 * will be where the user is, israel default will be he-IL, and user can change
 * it in user settings." So `locale` is a full tag — `he-IL`, `en-US`, `he-US`
 * — whose LANGUAGE picks the words and the direction and whose REGION formats
 * dates, numbers and money (`intlLocale`). In order:
 *
 *  1. **chosen** — in the account's settings (`profile.locale`, applied by
 *     `AccountLocale`) or the switch, remembered on the device;
 *  2. **the device** (since 2026-10-28) — the LANGUAGE is the computer's or
 *     phone's own (`navigator.languages`; when the interface has no words in
 *     it, the location's language, else English — below), the REGION is where the person is (`GET /geo`, the
 *     country Cloudflare sees, remembered for the next offline start), else
 *     the device tag's own region (`deviceLocale`, `@myrave/shared`).
 *
 * The user, 2026-10-28: "the default language from the system language, and
 * the default country from the location … the user can override this and
 * choose a different language". An English Mac in Israel opens en-IL, a
 * Hebrew one he-IL. This replaces the 2026-09-27 rule ("never the browser's
 * language") and the 2026-10-22 one (the language from the country). The phone
 * app's web view reports the phone's language, so the app, its widgets, the
 * always-on card and the shell's own words (which follow the page) all follow
 * it too; emails and pushes follow what the app last showed (`seenLocale`).
 *
 * ## Then the location's language, then English (the user, 2026-10-30)
 *
 * "The landing pages should use the same language rules: default by device,
 * fallback to location, and fallback to English" — then, asked whether the app
 * should too: "of course". On EVERY page a device language MyRave has no words
 * in falls back to the language of the COUNTRY the person is in (Cloudflare's,
 * the `myrave-geo-locale` meta / `GET /geo`) when MyRave has it, and only then
 * to English (`landingLocale`): a Swedish phone in Israel opens he-IL, in
 * Sweden en-SE.
 *
 * ## An address may name its language (step 73.11)
 *
 * `?lang=he-IL` (any case; a bare `he` is he-IL) wins over everything above
 * for that visit — it is how a search engine's result, or a shared link,
 * opens the page in the language it was found in — and is NEVER saved as a
 * choice. An invalid tag is ignored. The public pages' links carry it while it
 * is in the address (`languageAlternates.tsx`). Choosing a language with a
 * click ends it: the choice shows at once and `lang` leaves the address.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';
import {
  DEFAULT_LOCALE, LANG_PARAM, UI_LANGUAGES, directionOf, landingLocale, languageOf, localeFromParam, localized,
  normalizeLocale, regionOf,
  type Direction, type Locale,
} from '@myrave/shared';
import { readJson, writeJson } from '@/lib/storage';
import { readPlatformCookie, writePlatformCookie } from '@/lib/signOut';
import { api } from '@/lib/api';
import { dictionaryReady, loadDictionary, translate } from '@/i18n';
import { UiTextContext, type UiTranslate } from '@/ui/text';

/**
 * The languages the INTERFACE has words in (`UI_LANGUAGES`) — a language joins
 * only with its dictionary and `i18n.test.ts` passing for it, the rule that
 * keeps the 2026-09-14 half-RTL screen from coming back. Regions are not
 * limited: any country formats.
 */
const UI_LOCALES: readonly string[] = UI_LANGUAGES;

/** Storage: what the person CHOSE, and what their location last said. */
const CHOSEN = 'locale';
const LOCATED = 'geo-locale';

/** `device`: nothing chosen — the device's language, the location's region. */
export type LocaleSource = 'address' | 'chosen' | 'device';

interface LocaleValue {
  /** The full tag: `he-IL`, `en-US`, `he-US`. */
  locale: Locale;
  /** Its language: `he`, `en` — what the words are in. */
  language: string;
  /** Its region: `IL`, `US` — what formats dates, numbers and money. */
  region: string;
  direction: Direction;
  /** Where `locale` came from — the settings offer "Use my device's language" after a choice. */
  source: LocaleSource;
  /**
   * The CHOICE on record on this device (full tag), or null — what `setLocale` last kept, whatever is
   * on screen. `locale` can differ from it: while a language's table loads it shows English, and an
   * address's `?lang=` wins for the visit. `AccountLocale` compares the account's choice with THIS.
   */
  chosen: string | null;
  /**
   * Choose. A bare language (`'he'`, the switch's pills) keeps the current
   * region; a full tag sets both; `null` goes back to following location.
   */
  setLocale(locale: Locale | null): void;
  /** Pick the right string from a base value plus its translations. */
  t(base: string, translations?: Readonly<Record<string, string>>): string;
  /** The LANGUAGES offered. */
  available: readonly string[];
  /** The locale this page's address names (`?lang=he-IL`), while it does — never a saved choice. */
  addressLocale: string | null;
}

// Outside any provider (a screen rendered on its own): the app's base English,
// bare `en` — `intlLocale` keeps it day-month, as before step 73.
const LocaleContext = createContext<LocaleValue>({
  locale: DEFAULT_LOCALE,
  language: DEFAULT_LOCALE,
  region: 'US',
  direction: 'ltr',
  source: 'device',
  chosen: null,
  setLocale: () => {},
  t: (base) => base,
  available: UI_LOCALES,
  addressLocale: null,
});

/** The locale `?lang=` names in this page's address, canonical; null when none or invalid. */
export function localeInAddress(search = typeof window === 'undefined' ? '' : window.location.search): string | null {
  return localeFromParam(new URLSearchParams(search).get(LANG_PARAM));
}

/**
 * Is a person choosing right now — a click, a key, a change in a picker?
 * `setLocale` is also called by `AccountLocale` when the account's saved
 * value arrives, and THAT must not end an address's language: the address
 * wins for the visit. `window.event` is the event being dispatched (every
 * engine has it); outside one it is undefined.
 */
function choosingNow(): boolean {
  if (typeof window === 'undefined') return false;
  const e = (window as unknown as { event?: Event }).event;
  return Boolean(e && /^(click|change|input|keydown|keyup|pointerup|submit)$/.test(e.type));
}

/** The device's languages, most wanted first — the computer's or phone's own setting. */
function systemLanguages(): readonly string[] {
  if (typeof navigator === 'undefined') return [];
  return navigator.languages?.length ? navigator.languages : navigator.language ? [navigator.language] : [];
}

function stored(editionId: string | null, key: string): string | null {
  return normalizeLocale(readJson<string | null>(editionId, key, null));
}

/**
 * The CHOICE is one for every MyRave page on this device (the user, 2026-10-29: "default
 * language is changing between the apps" — on one computer). Each host is its own origin with
 * its own `localStorage`, so a choice made in a rave stayed in that rave while `app.`,
 * `account.` and `support.` kept theirs. The platform-domain cookie `myrave_locale` is what
 * every host reads (as `myrave_live_card` is); the host's own copy stands in where cookies are
 * refused.
 */
export const LOCALE_COOKIE = 'myrave_locale';
const LOCALE_COOKIE_AGE = 400 * 24 * 60 * 60;

function storedChoice(editionId: string | null): string | null {
  if (editionId === null) {
    const shared = readPlatformCookie(LOCALE_COOKIE);
    if (shared !== null) return shared === 'auto' ? null : normalizeLocale(decodeURIComponent(shared));
  }
  return stored(editionId, CHOSEN);
}

function keepChoice(editionId: string | null, tag: string | null): void {
  writeJson(editionId, CHOSEN, tag);
  // "auto", not a deletion: going back to the device's language must reach every host too —
  // with no cookie, another host's own older copy would win again.
  if (editionId === null) writePlatformCookie(LOCALE_COOKIE, tag ? encodeURIComponent(tag) : 'auto', LOCALE_COOKIE_AGE);
}

/**
 * The location's locale the Worker wrote into this page (`<meta name="myrave-geo-locale">`,
 * from Cloudflare's country — `worker/languageAlternates.ts`). A host visited for the first
 * time has nothing stored, and without this it showed English until `/geo` answered.
 */
export function pageLocation(doc: Document | null = typeof document === 'undefined' ? null : document): string | null {
  return normalizeLocale(doc?.querySelector('meta[name="myrave-geo-locale"]')?.getAttribute('content') ?? null);
}

export function LocaleProvider({
  editionId = null, children,
}: { editionId?: string | null; children: ReactNode }) {
  const [chosen, setChosen] = useState<string | null>(() => storedChoice(editionId));
  const [located, setLocated] = useState<string | null>(() => stored(editionId, LOCATED) ?? pageLocation());
  // Read once, at the start of the visit: the app's own navigation drops the
  // query, and the language must not change under the reader when it does.
  const [addressLocale, setAddressLocale] = useState<string | null>(() => localeInAddress());

  // A language chosen on another MyRave page (another tab, another host) shows here when this
  // tab comes back into view — one choice for the whole device.
  useEffect(() => {
    if (editionId !== null) return;
    const look = () => {
      if (document.visibilityState !== 'visible') return;
      const now = storedChoice(null);
      setChosen((was) => (was === now ? was : now));
    };
    document.addEventListener('visibilitychange', look);
    window.addEventListener('focus', look);
    return () => { document.removeEventListener('visibilitychange', look); window.removeEventListener('focus', look); };
  }, [editionId]);

  // Where the person is — asked on every start while nothing is chosen (the
  // same phone abroad gets that country's default); the last answer starts
  // the app offline. A failure keeps what is known.
  useEffect(() => {
    if (chosen || addressLocale) return;
    let live = true;
    void api.get<{ country: string | null; locale: string }>('/geo').then((geo) => {
      const next = normalizeLocale(geo?.locale);
      if (!live || !next) return;
      writeJson(editionId, LOCATED, next);
      setLocated(next);
    }, () => {});
    return () => { live = false; };
  }, [chosen, addressLocale, editionId]);

  // Nothing chosen: the device's language, the region from where the person is; a device
  // language MyRave lacks falls back to the location's, then English (`landingLocale`).
  const country = located ? regionOf(located) : null;
  const automatic = landingLocale(systemLanguages(), country);
  const wanted = addressLocale ?? chosen ?? automatic;
  const source: LocaleSource = addressLocale ? 'address' : chosen ? 'chosen' : 'device';

  // A language whose table has not arrived yet (it is a lazy chunk — see
  // `src/i18n`) is not switched to until it has: the whole page changes
  // language and direction at once, never English words under `dir="rtl"`.
  const [, setLoaded] = useState(0);
  const [booted, setBooted] = useState(() => dictionaryReady(wanted));
  useEffect(() => {
    if (dictionaryReady(wanted)) return;
    let live = true;
    // A table that cannot load opens the page in English rather than holding the launch screen.
    void loadDictionary(wanted).then(() => { if (live) setLoaded((n) => n + 1); }, () => { if (live) setBooted(true); });
    return () => { live = false; };
  }, [wanted]);
  const ready = dictionaryReady(wanted);
  const locale = ready ? wanted : `en-${regionOf(wanted) ?? 'US'}`;
  /*
   * At boot, the launch screen waits for the language's table (`data-boot-wait`, `app/splash.ts`):
   * the page opens in its language, never English first and then switched. Once anything has
   * been shown in it, a later change of language just switches when its table arrives.
   */
  useEffect(() => { if (ready) setBooted(true); }, [ready]);
  const language = languageOf(locale);
  const region = regionOf(locale) ?? 'US';
  const direction = directionOf(language);

  useEffect(() => {
    // On the document element, deliberately. See the note at the top.
    document.documentElement.setAttribute('dir', direction);
    document.documentElement.setAttribute('lang', locale);
  }, [direction, locale]);

  const setLocale = useCallback((next: Locale | null) => {
    const tag = next === null ? null
      : normalizeLocale(regionOf(next) ? next : `${languageOf(next)}-${regionOf(chosen ?? automatic) ?? 'US'}`);
    if (next !== null && !tag) return;
    setChosen(tag);
    keepChoice(editionId, tag);
    // A person's own choice ends the address's language, and takes `lang`
    // out of the address so a reload keeps what they chose.
    if (addressLocale && choosingNow()) {
      setAddressLocale(null);
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete(LANG_PARAM);
        window.history.replaceState(window.history.state, '', url.toString());
      } catch { /* no history: the state alone */ }
    }
  }, [chosen, automatic, editionId, addressLocale]);

  const value = useMemo<LocaleValue>(() => ({
    locale,
    language,
    region,
    direction,
    source,
    chosen,
    setLocale,
    t: (base, translations) => localized(base, translations, locale),
    available: UI_LOCALES,
    addressLocale,
  }), [locale, language, region, direction, source, chosen, setLocale, addressLocale]);

  // The design system's own words ("Close", "Try again") in this language —
  // `src/ui` builds alone and cannot import `@/i18n`, so it is handed this.
  const uiT = useCallback<UiTranslate>((english, vars) => translate(locale, english, vars), [locale]);

  return (
    <LocaleContext.Provider value={value}>
      <UiTextContext.Provider value={uiT}>
        {!booted && <span hidden data-boot-wait="language" />}
        {children}
      </UiTextContext.Provider>
    </LocaleContext.Provider>
  );
}

export function useLocale(): LocaleValue {
  return useContext(LocaleContext);
}
